import { useEffect, type Dispatch } from 'react';

import { getQueueItem } from '../../queue-module/queue-client.js';
import { clearPatientKeysFromIDB } from '../../crypto-module/clear-patient-keys-from-idb.js';
import { TechnicalErrorVariant } from '../technical-error-variant.js';
import type { CancelAction } from '../state-machine-feature/cancel-action.type.js';

import {
  MAX_CONSECUTIVE_NETWORK_RETRIES,
  MAX_POLL_ATTEMPTS,
  POLL_INTERVAL_MS,
} from './poll-constants.js';
import { handleStatusResponse } from './handle-status-response.js';
import { handlePollError, type PollErrorOutcome } from './handle-poll-error.js';

/**
 * Optionen für `usePollStatus`. `tenantId`, `queueItemId` und `privateKey`
 * kommen aus IDB (Submit-Pfad oder Recovery-Pfad). `enabled` schaltet den
 * Loop scharf — vor `LOADING` oder ohne Keypair bleibt der Hook still.
 * `dispatch` ist der Reducer-Eingang der State-Machine.
 *
 * Die drei Identifikations-Felder sind optional, damit Aufrufer den Hook auch
 * ohne fertige Daten konditional-frei mounten können (`enabled === false`).
 * Sobald `enabled === true` ist, MÜSSEN alle drei gesetzt sein — fehlen sie,
 * bleibt der Loop ebenfalls untätig (kein Throw, kein Dispatch).
 */
interface UsePollStatusOptions {
  readonly tenantId?: string;
  readonly queueItemId?: string;
  readonly privateKey?: CryptoKey;
  readonly enabled: boolean;
  readonly dispatch: Dispatch<CancelAction>;
}

interface LoopHandle {
  cancelled: boolean;
  controller: AbortController | null;
  timer: ReturnType<typeof setTimeout> | null;
  /**
   * Resolver der laufenden Warte-Promise (Review-Fund): löscht `stopLoop` nur
   * den Timer, wird die Promise aus `waitForNextTick` nie aufgelöst — ein
   * Unmount während eines 429-/Netzwerk-Backoffs ließe `runPollLoop`
   * dauerhaft offen (Speicherleck). Der Abbruch ruft ihn daher explizit auf;
   * die `cancelled`-Prüfung am Schleifenkopf beendet den Loop dann sofort.
   */
  wakeUp: (() => void) | null;
}

/**
 * React-Hook, der den Status-Polling-Loop gegen die Queue-API montiert.
 *
 * Verhalten:
 *  - `enabled === false`: Hook bleibt still (z. B. solange `state.screen !==
 *    'LOADING'`).
 *  - Rekursives `setTimeout` mit `POLL_INTERVAL_MS`. Kein `setInterval`, um
 *    Stacking bei langsamen Antworten zu vermeiden.
 *  - Pro Tick ein `getQueueItem` mit `AbortSignal`. `AbortError` ist ein
 *    legitimer Cleanup-Pfad und wird ignoriert.
 *  - Transiente Fehler brechen nicht ab (T-9): HTTP 429 wartet die
 *    `Retry-After`-Vorgabe ab, Netzwerkfehler werden bis zu
 *    `MAX_CONSECUTIVE_NETWORK_RETRIES`-mal in Folge wiederholt (Zähler
 *    resettet bei erfolgreichem Tick).
 *  - Nach `MAX_POLL_ATTEMPTS` ohne terminal-Status → `GOTO_TECHNICAL_ERROR`
 *    (generic) + IDB-Cleanup; das Cap gilt auch für Retry-Ticks.
 *  - Cleanup bei Unmount: laufender Fetch wird abortiert, pending Timer
 *    gelöscht.
 *
 * Side-Effects (Decrypt, Dispatch, IDB-Cleanup) sind in `handleStatusResponse`
 * und `handlePollError` ausgelagert, damit dieser Hook in 30 LOC bleibt.
 */
export function usePollStatus(options: UsePollStatusOptions): void {
  const { tenantId, queueItemId, privateKey, enabled, dispatch } = options;

  useEffect(() => {
    if (
      !enabled ||
      tenantId === undefined ||
      queueItemId === undefined ||
      privateKey === undefined
    ) {
      return undefined;
    }
    const handle: LoopHandle = { cancelled: false, controller: null, timer: null, wakeUp: null };
    void runPollLoop({ tenantId, queueItemId, privateKey, dispatch, handle });
    return () => stopLoop(handle);
  }, [enabled, tenantId, queueItemId, privateKey, dispatch]);
}

interface LoopArgs {
  readonly tenantId: string;
  readonly queueItemId: string;
  readonly privateKey: CryptoKey;
  readonly dispatch: Dispatch<CancelAction>;
  readonly handle: LoopHandle;
}

type TickOutcome = { readonly kind: 'continue' | 'terminal' } | PollErrorOutcome;

async function runPollLoop(args: LoopArgs): Promise<void> {
  let consecutiveNetworkFailures = 0;
  for (let attempt = 1; attempt <= MAX_POLL_ATTEMPTS; attempt += 1) {
    if (args.handle.cancelled) {
      return;
    }
    const tick = await runSingleTick(args);
    if (tick.kind === 'terminal' || tick.kind === 'abort' || args.handle.cancelled) {
      return;
    }
    if (tick.kind === 'retry-network') {
      consecutiveNetworkFailures += 1;
      if (consecutiveNetworkFailures > MAX_CONSECUTIVE_NETWORK_RETRIES) {
        await dispatchTerminalError(args);
        return;
      }
      await waitForNextTick(args.handle, POLL_INTERVAL_MS);
      continue;
    }
    if (tick.kind === 'retry-rate-limited') {
      await waitForNextTick(args.handle, tick.retryAfterMs);
      continue;
    }
    consecutiveNetworkFailures = 0;
    await waitForNextTick(args.handle, POLL_INTERVAL_MS);
  }
  await dispatchTerminalError(args);
}

async function runSingleTick(args: LoopArgs): Promise<TickOutcome> {
  const controller = new AbortController();
  args.handle.controller = controller;
  try {
    const response = await getQueueItem({
      tenantId: args.tenantId,
      id: args.queueItemId,
      signal: controller.signal,
    });
    if (args.handle.cancelled) {
      return { kind: 'terminal' };
    }
    const statusOutcome = await handleStatusResponse({
      response,
      privateKey: args.privateKey,
      dispatch: args.dispatch,
    });
    return { kind: statusOutcome };
  } catch (error) {
    if (args.handle.cancelled) {
      return { kind: 'terminal' };
    }
    return await handlePollError({ error, dispatch: args.dispatch });
  } finally {
    args.handle.controller = null;
  }
}

function waitForNextTick(handle: LoopHandle, delayMs: number): Promise<void> {
  return new Promise<void>((resolve) => {
    handle.wakeUp = resolve;
    handle.timer = setTimeout(() => {
      handle.timer = null;
      handle.wakeUp = null;
      resolve();
    }, delayMs);
  });
}

/**
 * Endzustand aus dem Loop selbst: Versuchs-Cap erschöpft oder zu viele
 * Netzwerkfehler in Folge. Gleicher Effekt wie der terminale Fehlerpfad in
 * `handlePollError` (Technical-Error + IDB-Cleanup).
 */
async function dispatchTerminalError(args: LoopArgs): Promise<void> {
  if (args.handle.cancelled) {
    return;
  }
  args.dispatch({
    type: 'GOTO_TECHNICAL_ERROR',
    variant: TechnicalErrorVariant.Generic,
  });
  try {
    await clearPatientKeysFromIDB();
  } catch {
    // Cleanup-Fehler darf den End-State nicht verschlucken.
  }
}

function stopLoop(handle: LoopHandle): void {
  handle.cancelled = true;
  if (handle.timer !== null) {
    clearTimeout(handle.timer);
    handle.timer = null;
  }
  // Wartende Promise auflösen, damit `runPollLoop` regulär enden kann —
  // die `cancelled`-Prüfung am Schleifenkopf stoppt ihn sofort danach.
  handle.wakeUp?.();
  handle.wakeUp = null;
  handle.controller?.abort();
  handle.controller = null;
}
